import { Box } from "@mui/material";
import Typography from "@mui/material/Typography";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { useMember } from "api/members";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import { BarChartPercentage } from "components/Table/BarChartPercentage";
import { OLTable, Row, createHeadItems } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { ExternalLinkTooltipContent } from "components/Tooltips/ExternalLinkTooltip";
import { TooltipWithIcon } from "components/Tooltips/TooltipWithIcon";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { USER_ACHIEVEMENTS_STATUS_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { formatNumberValue } from "utilities/numbers";
import { AchievementDetailsDrawer } from "./AchievementDetailsDrawer/AchievementDetailsDrawer";
import { ConsecutiveRuleTooltip } from "./AchievementDetailsDrawer/ConsecutiveRuleTooltip";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    filtersWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: theme.spacing(4),
    },
    barChart: {
      marginLeft: theme.spacing(7),
      width: theme.spacing(29),
    },
    currentProgress: {
      display: "flex",
      justifyContent: "end",
    },
    link: {
      color: theme.primary500,
      cursor: "pointer",
      textDecoration: "underline",
    },
    currentProgressTooltip: {
      whiteSpace: "break-spaces",
    },
    warnIcon: {
      color: theme.orange500,
      height: "15px",
      opacity: 1,
      width: "15px",
    },
    tableRow: {
      alignItems: "center",
      display: "flex",
      gap: theme.spacing(6),
    },
    header: {
      color: theme.gray800,
      fontSize: 14,
      fontWeight: 500,
      lineHeight: "22px",
      margin: theme.spacing(12, 0, 8, 0),
      textTransform: "uppercase",
      textWrap: "nowrap",
    },
  }),
);

export function Achievements() {
  const { id: memberID } = useParams<{ id: string; page: string }>();
  const classes = useStyles();
  const { message } = useMessage();
  const { listing, updateListing } = useListing();
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedAchievementId, setSelectedAchievementId] = useState("");
  const { useMembersAchievements } = useMember(memberID);
  const tables = useAppSelector(selectTables);
  const appDispatch = useAppDispatch();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[USER_ACHIEVEMENTS_STATUS_LIST_ID]?.filters || [],
  );
  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      USER_ACHIEVEMENTS_STATUS_LIST_ID,
    );

    setFilters(filters);
  };
  const { data: membersAchievements, error } = useMembersAchievements({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "name",
    dynamicSearch,
  });

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && membersAchievements) {
      setPersistentFilters(filters, USER_ACHIEVEMENTS_STATUS_LIST_ID);
    }
    setPersistentFilters(filters, USER_ACHIEVEMENTS_STATUS_LIST_ID);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, error, membersAchievements]);

  const headItems = createHeadItems([
    {
      headerText: message({ id: "achievementName" }),
      property: "name",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "currentProgress" }),
      isSortable: false,
      property: "currentProgress",
      tooltip: (
        <ExternalLinkTooltipContent
          externalLink="https://help.openloyalty.io/main-features/members/member-list#achievement-status"
          name="currentProgress"
          text={
            <span className={classes.currentProgressTooltip}>
              {message({ id: "currentProgressInfo" })}
            </span>
          }
        />
      ),
    },
    {
      headerText: message({ id: "completionStatus" }),
      isSortable: false,
      property: "completionStatus",
      align: "right",
    },
  ]);

  const items = membersAchievements?.items
    ? createItems(membersAchievements.items)
    : [];

  function createItems(items: any) {
    return items.map((item: any) => {
      const consecutivePeriods =
        item.memberProgress.rules[0].consecutivePeriods;
      const completedConsecutivePeriods =
        item.memberProgress.rules[0].completedConsecutivePeriods;
      const currentPeriodValue =
        item.memberProgress.rules[0].currentPeriodValue;
      const periodGoal = item.memberProgress.rules[0].periodGoal;

      const isAchievementConsecutive = consecutivePeriods !== undefined;

      return {
        id: item.achievementId,
        cells: [
          {
            content: (
              <div className={classes.tableRow}>
                {item.editedByAdminAt && (
                  <TooltipWithIcon
                    className={classes.warnIcon}
                    icon={<AlertTriangleIcon />}
                    isWhite
                    placement="bottom-start"
                    text={
                      <ExternalLinkTooltipContent
                        name="manuallyChangedProgress"
                        text={message(
                          { id: "manuallyChangedProgressInfo" },
                          { date: OLDate(item.editedByAdminAt) },
                        )}
                      />
                    }
                  />
                )}
                {item.achievementName}
              </div>
            ),
            property: "name",
          },
          {
            align: "right",
            content:
              item.memberProgress.rules.length === 1 ? (
                <span className={classes.currentProgress}>
                  <Box sx={{ fontWeight: "bold" }}>
                    {formatNumberValue(currentPeriodValue, "")}
                  </Box>
                  {`/${formatNumberValue(periodGoal, "")}`}
                  {isAchievementConsecutive && <ConsecutiveRuleTooltip />}
                  {!!periodGoal && (
                    <BarChartPercentage
                      tooltip={
                        isAchievementConsecutive
                          ? `${message({
                              id: "currentPeriod",
                            })}: ${formatNumberValue(
                              completedConsecutivePeriods,
                              "",
                            )} / ${formatNumberValue(consecutivePeriods, "")}`
                          : `${formatNumberValue(
                              currentPeriodValue,
                            )} / ${formatNumberValue(periodGoal, "")}`
                      }
                      barChartData={[
                        {
                          itemShare: isAchievementConsecutive
                            ? completedConsecutivePeriods
                            : currentPeriodValue,
                        },
                        {
                          itemShare: isAchievementConsecutive
                            ? consecutivePeriods
                            : periodGoal - currentPeriodValue,
                        },
                      ]}
                      className={classes.barChart}
                    />
                  )}
                </span>
              ) : (
                <span className={classes.link} data-test-id="see-x-rules">
                  {message(
                    { id: "seeXRules" },
                    { x: item.memberProgress.rules.length },
                  )}
                </span>
              ),
            property: "currentProgress",
          },
          {
            align: "right",
            content: item.memberProgress.completedCount,
            property: "completionStatus",
          },
        ],
        metadata: { data: item },
        testId: "achievements-status-table-row",
      };
    });
  }

  const achievementPogressDetails =
    membersAchievements &&
    membersAchievements.items.find(
      (achievement) => achievement.achievementId === selectedAchievementId,
    );

  return (
    <>
      <Typography
        mb={6}
        data-test-id="single-member-achievements-status-title"
        className={classes.header}
      >
        {message({ id: "achievements" })}
      </Typography>
      <Box className={classes.filtersWrapper}>
        <FiltersV2
          savedFilters={getNewFiltersOnly(filters)}
          donutLabel={message({ id: "results" })}
          setFilters={setSelectedFilters}
          total={membersAchievements?.total}
          properties={[
            {
              query: "achievementId",
              label: message({ id: "achievementId" }),
              type: "text",
            },
            {
              query: "completionCount",
              label: message({ id: "completion" }),
              type: "number",
            },
            {
              query: "createdAt",
              label: message({ id: "createdOn" }),
              type: "datetime",
            },
            {
              query: "name",
              label: message({ id: "name" }),
              type: "achievement-name",
            },
          ]}
        />
        <DynamicSearch
          property="name"
          setDynamicSearch={setDynamicSearch}
          onSearch={() =>
            appDispatch(
              clearTablePage({ tableName: USER_ACHIEVEMENTS_STATUS_LIST_ID }),
            )
          }
        />
      </Box>
      <OLTable
        setColumnOrdering={setColumnOrdering}
        tableName={USER_ACHIEVEMENTS_STATUS_LIST_ID}
        onRowClick={(id) => {
          setDrawerOpen(true);
          setSelectedAchievementId(id);
        }}
        actions={{
          body: [
            {
              action: (_, item?: Row) => {
                setDrawerOpen(true);
                setSelectedAchievementId(item?.id as string);
              },
              label: message({ id: "achievementDetails" }),
            },
          ],
        }}
        data-test-id="single-member-achievements-status"
        columns={membersAchievements && headItems}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        rows={membersAchievements && items}
        sort={{
          order: listing.order || "asc",
          property: listing.property || "name",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        pagination={{
          count: calculateCount(
            membersAchievements?.total.filtered,
            membersAchievements?.total.all,
          ),
          onChangePage: (page) =>
            updateListing({ payload: page, type: ListingActions.CHANGE_PAGE }),
          onChangeRowsPerPage: (rows) =>
            updateListing({
              payload: rows,
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
            }),
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
      />
      {isDrawerOpen && achievementPogressDetails && (
        <AchievementDetailsDrawer
          open={isDrawerOpen}
          achievementId={selectedAchievementId}
          close={() => setDrawerOpen(false)}
          achievementPogressDetails={achievementPogressDetails}
        />
      )}
    </>
  );
}
